import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from './firebase';

// Регистрация с email/password
export const registerWithEmail = async (email, password, displayName) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Обновляем профиль пользователя
    await updateProfile(user, {
      displayName: displayName || email.split('@')[0]
    });

    // Создаем документ пользователя в Firestore
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: displayName || email.split('@')[0],
      photoURL: user.photoURL || null,
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
      preferences: {
        language: 'en',
        currency: 'USD',
        notifications: true
      }
    });

    return { user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// Вход с email/password
export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Обновляем время последнего входа
    await setDoc(doc(db, 'users', user.uid), {
      lastLoginAt: new Date().toISOString()
    }, { merge: true });

    return { user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// Вход через Google (использует redirect для избежания проблем с CORS)
export const loginWithGoogle = async () => {
  try {
    // Пытаемся использовать popup, если не получается - используем redirect
    let result;
    try {
      result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
      await saveOrUpdateUser(user);
      return { user, error: null };
    } catch (popupError) {
      // Если popup заблокирован или есть проблемы с CORS, используем redirect
      const popupErrorCodes = [
        'auth/popup-blocked',
        'auth/popup-closed-by-user',
        'auth/cancelled-popup-request'
      ];
      
      // Проверяем код ошибки или сообщение об ошибке CORS
      const isPopupError = popupErrorCodes.includes(popupError.code) ||
                          popupError.message?.includes('Cross-Origin-Opener-Policy') ||
                          popupError.message?.includes('window.closed');
      
      if (isPopupError) {
        // Используем redirect вместо popup
        await signInWithRedirect(auth, googleProvider);
        // При redirect функция вернется после редиректа, поэтому возвращаем специальный флаг
        return { user: null, error: null, redirect: true };
      }
      // Если это другая ошибка, пробрасываем её дальше
      throw popupError;
    }
  } catch (error) {
    // Если это не ошибка popup, возвращаем ошибку
    return { user: null, error: error.message || 'Ошибка входа через Google' };
  }
};

// Обработка результата redirect после возврата
export const handleGoogleRedirect = async () => {
  try {
    const result = await getRedirectResult(auth);
    if (result && result.user) {
      await saveOrUpdateUser(result.user);
      return { user: result.user, error: null };
    }
    return { user: null, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// Вспомогательная функция для сохранения/обновления пользователя
const saveOrUpdateUser = async (user) => {
    // Проверяем, существует ли пользователь в Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));

    if (!userDoc.exists()) {
      // Создаем нового пользователя
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        preferences: {
          language: 'en',
          currency: 'USD',
          notifications: true
        }
      });
    } else {
      // Обновляем время последнего входа
      await setDoc(doc(db, 'users', user.uid), {
        lastLoginAt: new Date().toISOString()
      }, { merge: true });
  }
};

// Выход
export const logout = async () => {
  try {
    await signOut(auth);
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

// Сброс пароля
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

// Получение данных пользователя из Firestore
export const getUserData = async (uid) => {
  const startTime = performance.now();
  try {
    if (!uid) {
      return { userData: null, error: 'User ID is required' };
    }
    
    console.log(`[getUserData] Начало запроса для uid: ${uid}`);
    const userDoc = await getDoc(doc(db, 'users', uid));
    const duration = performance.now() - startTime;
    
    if (userDoc.exists()) {
      console.log(`[getUserData] Данные получены за ${duration.toFixed(2)}ms`);
      return { userData: userDoc.data(), error: null };
    } else {
      console.log(`[getUserData] Документ не найден за ${duration.toFixed(2)}ms`);
      return { userData: null, error: 'User not found' };
    }
  } catch (error) {
    const duration = performance.now() - startTime;
    // Обрабатываем офлайн ошибки более мягко
    const errorCode = error.code || '';
    const errorMessage = error.message || '';
    const isOfflineError = 
      errorCode === 'unavailable' || 
      errorCode === 'failed-precondition' ||
      errorMessage.toLowerCase().includes('offline') ||
      errorMessage.toLowerCase().includes('client is offline');
    
    if (isOfflineError) {
      // Если клиент офлайн, возвращаем null без ошибки
      // Приложение будет использовать базовые данные из auth
      console.log(`[getUserData] Офлайн режим (${duration.toFixed(2)}ms)`);
      return { userData: null, error: null, offline: true };
    }
    // Для других ошибок логируем предупреждение, но не блокируем работу приложения
    console.warn(`[getUserData] Ошибка за ${duration.toFixed(2)}ms:`, errorMessage || errorCode);
    return { userData: null, error: null, offline: false };
  }
};

// Обновление данных пользователя
export const updateUserData = async (uid, data) => {
  try {
    await setDoc(doc(db, 'users', uid), data, { merge: true });
    return { error: null };
  } catch (error) {
    return { error: error.message };
  }
};

// Слушатель состояния аутентификации
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
}; 